<template>
  <section
    v-if="state.search.show"
    class="cxco-c-search"
    :data-state="`${openList && searchValue?.length > 0 && searchResults?.length > 0 ? 'open' : 'closed'} ${inputState}`"
    role="search"
    aria-label="Search FAQs"
  >
    <div class="cxco-c-search__group" ref="searchDiv">
      <label for="searchBar" class="sr-only">
        {{ state.search.placeholder }}
      </label>
      <div class="cxco-c-search__field">
        <Icon class="cxco-c-search__icon" :name="'search'" aria-hidden="true" />
        <input
          type="text"
          id="searchBar"
          name="searchBar"
          class="cxco-c-search__input"
          :aria-expanded="openList && searchValue?.length > 0 && searchResults?.length > 0"
          aria-controls="search-results"
          aria-describedby="search-description"
          aria-autocomplete="list"
          v-model="searchValue"
          :placeholder="state.search.placeholder"
          @keyup.enter="clickSearchResult(searchResults[0])"
          @focus="openSearchList(true)"
          @input="changeInput"
          @focusout="checkFocus"
        />
        <button
          class="cxco-c-button cxco-c-button--outlined"
          @click="submitSearch"
          @focusout="checkFocus"
        >
          {{ state.search.searchButton }}
        </button>
      </div>
      <span id="search-description" class="sr-only">
        Enter search terms to find FAQs.
      </span>
      <!-- Live region for search results -->
      <div 
        class="sr-only" 
        role="status" 
        aria-live="polite"
        aria-atomic="true"
      >
        {{ searchAnnouncement }}
      </div>
      <ul
        id="search-results"
        @focusout="checkFocus"
        class="cxco-c-search__list"
        :aria-label="`Search results: ${searchResults.length} items found`"
        v-show="openList && searchValue?.length > 0 && searchResults?.length > 0"
      >
        <li
          v-for="searchResult in searchResults"
          :key="searchResult.id"
          class="cxco-c-search__list-item"
        >
          <button
            :aria-label="searchResult.question"
            :aria-pressed="question === searchResult.question"
            class="cxco-c-search__list-button"
            @mousedown="clickSearchResult(searchResult)"
            @keyup.enter="clickSearchResult(searchResult)"
          >
            {{ searchResult.question }}
          </button>
        </li>
      </ul>
    </div>
    <div class="cxco-c-search__result" v-if="question.length > 0" >
      <header class="cxco-c-search__header">
        <h2 class="cxco-c-search__question" ref="searchResult" tabindex="0">{{ question }}</h2>
        <button
          @click="closeResult()"
          aria-label="Close button"
          class="cxco-c-search__close"
        >
          <Icon :name="'close'" />
        </button>
      </header>
      <section
        v-if="searchAnswer?.length > 0"
        class="cxco-c-search__answer"
        v-html="searchAnswer"
      ></section>
      <section
        v-else-if="searchAnswerLoading.show"
        class="cxco-c-search__answer"
      >
        <p>
          <em>{{ searchAnswerLoading.text }}</em>
        </p>
      </section>
    </div>
  </section>
</template>
<script>
import { inject } from "vue";
import Icon from "./icon";
import { debounce } from "../utils";

export default {
  components: { Icon },
  setup() {
    const state = inject("state");
    const actions = inject("actions");
    return { state, actions };
  },
  data() {
    return {
      question: "",
      inputState: "",
      openList: false,
      searchAnswerLoading: {
        text: this.state.searchAnswerLoadingText,
        show: false,
      },
      searchingText: this.state.searchingText,
      noResultsText: this.state.noResultsText,
      searchValue: "",
      isSearching: false
    };
  },
  computed: {
    searchResults() {
      if (this.isSearching) {
        return [{ question: this.searchingText }];
      }

      if(!Array.isArray(this.state.searchResults?.matches)) return [];
      
      return this.state.searchResults.matches.length > 0
        ? this.state.searchResults.matches
        : [{ question: this.noResultsText }];
    },
    searchAnswer() {
      return this.state.searchAnswer.data?.answer;
    },
    searchAnnouncement() {
      if (!this.searchValue) return '';
      
      if (this.isSearching) {
        return this.searchingText;
      }

      // Check if we've actually completed a search 
      if (this.state.searchResults?.matches !== undefined) {
        if (this.state.searchResults.matches.length === 0) {
          return `No results found for ${this.searchValue}`;
        }
        return `${this.state.searchResults.matches.length} results found for ${this.searchValue}`;
      }

      return ''; // Return empty string until search completes
    },
  },
  watch: {
     // Watch for when search result becomes visible
     question(newValue) {
       if (newValue.length > 0) {
         this.$nextTick(() => {
           if (this.$refs.searchResult) {
             this.$refs.searchResult.focus();
           }
         });
       }
     }
    },
  methods: {
    async submitSearch() {
      if (this.searchValue.length > 0) {
        try {
          this.isSearching = true;
          await this.actions.searchFaq(this.searchValue);
        } finally {
          this.isSearching = false;
          this.openList = true;
        }
      }
    },
    openSearchList(open) {
      this.inputState = 'active'
      this.openList = open;
    },
    checkFocus(e) {
      if (!this.$refs.searchDiv?.contains(e.relatedTarget)) {
        this.openSearchList(false);
      }

      this.inputState = 'inactive'
    },
    clickSearchResult(result) {
      //Use mousedown and keyup enter event for Safari support. Safari does not focus on buttons and event therefore does not bubble.
      // mouseDown and keyup event happen before focusOut and makes possible to have both working in all browsers
      this.actions.resetSearchAnswer();
      this.searchAnswerLoading.show = true;
      this.question = result.question;
      this.openSearchList(false);
      this.actions.selectSearchResult(result);
    },
    changeInput() {
      // Clear results immediately
      this.state.searchResults.matches = undefined;
      
      // Debounce the actual search
      this.debounceInput();
    },
    debounceInput: debounce(async function() {
      await this.submitSearch();
    }, 400),
    closeResult() {
      this.actions.resetSearchAnswer();
      this.searchAnswerLoading.show = false;
      this.question = "";
    },
  },
};
</script>
